import { useState, type CSSProperties, type ReactNode } from 'react';
import { BrandMark } from '../components/AppShell';
import { Link } from '../app/Link';
import { href, type Route } from '../app/router';
import { PaneResizer, PaneWidthsProvider, usePaneWidths } from './PaneResizer';

export interface StudioShellProps {
  workspaceId: string;
  workspaceName: string;
  navigate(route: Route, options?: { replace?: boolean }): void;
  /** 左の暗色の列（AI との対話）。 */
  chat: ReactNode;
  /** 右の明色の列（成果物）。 */
  children: ReactNode;
  /** 渡したときだけヘッダーにログアウトを出す。認証が無効な local では渡さない。 */
  onLogout?: () => void;
}

/**
 * Studio の外枠（Claude Design の Studio Frame）。55px のヘッダー、53px のレール、チャット、stage。
 * 狭い画面ではチャットを stage に重ね、ヘッダーのボタンで開閉する（開閉は CSS が .studio-shell-chat-open を見る）。
 * チャットとインスペクタの幅は利用者が変えられる（U4）。覚えた幅を CSS 変数で配り、つまみは列の端に置く。
 */
export function StudioShell(props: StudioShellProps) {
  return (
    <PaneWidthsProvider>
      <StudioFrame {...props} />
    </PaneWidthsProvider>
  );
}

function StudioFrame({ workspaceId, workspaceName, navigate, chat, children, onLogout }: StudioShellProps) {
  const [chatOpen, setChatOpen] = useState(false);
  const { widths } = usePaneWidths();
  // インスペクタは stage の中（Canvas）にあるので、幅は CSS 変数で配る。
  const widthVars = {
    '--studio-chat-width': `${widths.chat}px`,
    '--studio-inspector-width': `${widths.inspector}px`,
  } as CSSProperties;
  return (
    <div className={chatOpen ? 'studio-shell studio-shell-chat-open' : 'studio-shell'} style={widthVars}>
      <header className="studio-header">
        <a className="studio-brand" href={href({ name: 'workspaces' })}>
          <BrandMark />
          <span className="studio-brand-name">microapp基盤</span>
        </a>
        <span className="studio-header-separator" aria-hidden="true">
          /
        </span>
        <Link className="studio-workspace-name" to={{ name: 'workspace', workspaceId }} onNavigate={navigate}>
          {workspaceName || 'Workspace'}
        </Link>
        <span className="studio-header-lead">目的ごとの、アプリづくりの場所</span>
        <span className="spacer" />
        <button
          type="button"
          className="studio-chat-toggle"
          aria-label="AI対話を開閉"
          aria-expanded={chatOpen}
          onClick={() => setChatOpen((open) => !open)}
        >
          AI
        </button>
        {onLogout === undefined ? null : (
          <button type="button" className="nav-item" onClick={onLogout}>
            ログアウト
          </button>
        )}
      </header>
      <nav className="studio-rail" aria-label="Workspace のメニュー">
        <Link className="studio-rail-item" to={{ name: 'workspace', workspaceId }} onNavigate={navigate}>
          <RailIcon path="M3 3h8v8H3zM13 3h8v8h-8zM3 13h8v8H3zM13 13h8v8h-8z" />
          <span className="studio-rail-label">Workspace の構成</span>
        </Link>
        <Link className="studio-rail-item" to={{ name: 'knowledge', workspaceId }} onNavigate={navigate}>
          <RailIcon path="M5 3h10l4 4v14H5zM15 3v4h4M8 12h8M8 16h8" />
          <span className="studio-rail-label">知識ソース</span>
        </Link>
      </nav>
      <section className="studio-chat" aria-label="AI との対話">
        {chat}
        <PaneResizer pane="chat" edge="end" label="AI との対話の幅" />
      </section>
      <section className="studio-stage" aria-label="成果物">
        {children}
      </section>
    </div>
  );
}

function RailIcon({ path }: { path: string }) {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false">
      <path d={path} fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
    </svg>
  );
}
